import {
  redrawFragment,
  LitheSpecificElement,
  LitheFC,
} from '@wheelbox/lithejs-engine';
import { goto, withRouteParams } from '@wheelbox/lithejs-router';
import withTranslations, { withCurrentLanguage } from '@wheelbox/lithejs-i18n';
import { FileTextIcon } from '@wheelbox/lithejs-lucide';

import withPageTitle from '@lib/page';
import {
  fetchDocsIndex,
  fetchDoc,
  type DocData,
  type DocMeta,
} from '@lib/docs-loader';
import { updateSlice, withSlice } from '@state/index';

import { APP_DOCS_SLICE } from '@constants/app';

import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarTrigger,
} from '@components/ui';
import DocsContentView from './content';

import { type DocsPageSlice } from '@state/index.model';

const DocsPage: LitheFC = ({
  id = 'docs-page',
  testId = 'docs-page',
}): LitheSpecificElement => {
  withPageTitle('LitheJS | Documentation');
  const lng = withCurrentLanguage();
  const t = withTranslations('Docs');
  const { section } = withRouteParams();
  const state = withSlice<DocsPageSlice>(APP_DOCS_SLICE);

  let docData: DocData | null = null;
  let isLoading = !!section;

  const loadDoc = async (filename: string) => {
    isLoading = true;
    redrawFragment(`${id}-docs-content`);

    try {
      docData = await fetchDoc(filename, lng);
    } catch (error) {
      console.error('Failed to load documentation:', error);
      docData = null;
    }

    isLoading = false;
    redrawFragment(`${id}-docs-content`);
  };

  const getPreviousAndNextDocs = (): {
    previous?: DocMeta;
    next?: DocMeta;
  } => {
    const sections = state.sections as DocMeta[];
    const activeIndex = sections.findIndex((item) => section === item.filename);

    return {
      previous: activeIndex > 0 ? sections[activeIndex - 1] : undefined,
      next:
        activeIndex > -1 && activeIndex < sections.length - 1
          ? sections[activeIndex + 1]
          : undefined,
    };
  };

  const onMountHandler = async () => {
    if (state.sections.length === 0 || state.language !== lng) {
      try {
        const sections = await fetchDocsIndex(lng);
        updateSlice<DocsPageSlice>(APP_DOCS_SLICE, {
          sections,
          language: lng,
        });
        redrawFragment(`${id}-sidebar-menu`);
        if (section) {
          redrawFragment(`${id}-breadcrumb-section-title`);
        }
      } catch (error) {
        console.error('Failed to load docs index:', error);
      }
    }

    if (section) {
      loadDoc(section);
    }
  };

  const getSectionTitle = () => {
    const sections = state.sections as DocMeta[];
    const sectionData = sections.find((s) => s.filename === section);
    return sectionData?.title || section;
  };

  const handleSectionSelect = (filename: string) => {
    goto(`/docs/${filename}`);
  };

  const renderSidebarMenu = (): LitheSpecificElement => {
    const sections = state.sections as DocMeta[];

    if (sections.length === 0) {
      return [
        'div',
        {
          id: `${id}-sidebar-menu`,
          class: 'p-4 text-muted-foreground text-sm',
        },
        'Loading...',
      ];
    }

    return [
      'div',
      { id: `${id}-sidebar-menu` },
      SidebarMenu(
        {},
        ...sections.map((docSection) =>
          SidebarMenuItem(
            {},
            SidebarMenuButton(
              {
                isActive: section === docSection.filename,
                onclick: () => handleSectionSelect(docSection.filename),
              },
              FileTextIcon({ size: 16 }),
              ['span', docSection.title]
            )
          )
        )
      ),
    ];
  };

  return SidebarProvider(
    {
      id,
      defaultOpen: true,
    },
    [
      'div',
      {
        'data-testid': testId,
        class: 'flex w-full h-[calc(100vh-4rem)]',
        onmount: onMountHandler,
      },
      // Sidebar
      Sidebar(
        {
          side: 'left',
          variant: 'sidebar',
          collapsible: 'offcanvas',
          classes: 'border-r-0',
        },
        SidebarContent(
          { classes: 'pt-4' },
          SidebarGroup(
            {},
            SidebarGroupLabel({}, t('packages')),
            SidebarGroupContent({}, () => renderSidebarMenu())
          )
        )
      ),

      // Main content area
      SidebarInset(
        { classes: 'flex-1 flex flex-col overflow-hidden' },
        // Header with breadcrumb and trigger
        [
          'header',
          {
            class: 'flex h-14 shrink-0 items-center gap-4 bg-background px-4',
          },
          SidebarTrigger({ classes: 'md:hidden' }),
          Breadcrumb(
            { id: `${id}-breadcrumb` },
            BreadcrumbList(
              { id: `${id}-breadcrumb-list` },
              BreadcrumbItem(
                { id: `${id}-breadcrumb-home` },
                BreadcrumbLink(
                  { id: `${id}-breadcrumb-home-link`, href: '/' },
                  'Home'
                )
              ),
              BreadcrumbSeparator({ id: `${id}-breadcrumb-sep-1` }),
              section
                ? BreadcrumbItem(
                    { id: `${id}-breadcrumb-docs` },
                    BreadcrumbLink(
                      {
                        id: `${id}-breadcrumb-docs-link`,
                        href: '/docs',
                      },
                      t('title')
                    )
                  )
                : BreadcrumbItem(
                    { id: `${id}-breadcrumb-docs` },
                    BreadcrumbPage(
                      { id: `${id}-breadcrumb-docs-page` },
                      t('title')
                    )
                  ),
              ...(section
                ? [
                    BreadcrumbSeparator({ id: `${id}-breadcrumb-sep-2` }),
                    BreadcrumbItem({ id: `${id}-breadcrumb-section` }, () =>
                      BreadcrumbPage(
                        { id: `${id}-breadcrumb-section-title` },
                        getSectionTitle()
                      )
                    ),
                  ]
                : [])
            )
          ),
        ],

        // Docs content
        [
          'main',
          { class: 'flex-1 overflow-auto p-6' },
          () =>
            DocsContentView({
              id: `${id}-docs-content`,
              data: docData,
              sections: state.sections as DocMeta[],
              isLoading,
              ...getPreviousAndNextDocs(),
              onselect: handleSectionSelect,
            }),
        ]
      ),
    ]
  );
};

export default DocsPage;
